"use client";

/**
 * ROI summary card with net value, range bar, and readout metrics.
 *
 * Extracted from `compass-assumptions.jsx:117-127` (`.asm-roi`, `.asm-roinet`,
 * `.asm-rangebar`, `.asm-readouts`).
 */

export interface RoiBandProps {
  net?: string;
  up?: string;
  down?: string;
  min?: string;
  max?: string;
  midPct?: number;
  readouts?: [string, string][];
  calculated?: boolean;
  missingCount?: number;
}

export function RoiBand({
  net,
  up,
  down,
  min,
  max,
  midPct = 50,
  readouts,
  calculated = true,
  missingCount = 0,
}: RoiBandProps) {
  return (
    <div className="shrink-0 w-[280px] bg-(--bg-white) border border-(--border-subtle) rounded-(--radius-container) px-4 py-[13px]">
      <div className="text-[11px] tracking-[.1em] uppercase text-(--fg-muted) font-semibold">
        Net ROI
      </div>
      <div className="text-[25px] font-bold tracking-[-0.02em] text-(--fg-primary) mt-0.5 flex items-baseline gap-[10px]">
        {calculated ? net : "—"}
        {calculated && (up || down) && (
          <span className="flex flex-col text-[11.5px] font-semibold leading-[1.2]">
            {up && <span className="text-(--status-success-text)">{up}</span>}
            {down && <span className="text-(--empty)">{down}</span>}
          </span>
        )}
      </div>

      {calculated ? (
        <>
          {/* Range bar */}
          <div className="mt-[10px]">
            <div className="relative h-1.5 rounded-full bg-(--border-default)">
              <span
                className="absolute -top-[3px] w-[3px] h-3 rounded-sm bg-(--brand-navy) -translate-x-1/2"
                style={{ left: `${midPct}%` }}
              />
            </div>
            <div className="flex justify-between text-[11px] text-(--fg-muted) mt-1.5">
              <span>{min}</span>
              <span>{max}</span>
            </div>
          </div>

          {/* Readout metrics */}
          {readouts && readouts.length > 0 && (
            <div className="flex gap-[10px] mt-[11px] pt-[10px] border-t border-(--border-subtle)">
              {readouts.map(([k, v], i) => (
                <div key={i} className="flex-1">
                  <div className="text-[10px] tracking-[.05em] uppercase text-(--fg-muted)">
                    {k}
                  </div>
                  <div className="text-[14px] font-bold text-(--fg-primary) mt-0.5 tracking-[-0.01em]">
                    {v}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="mt-[10px] rounded-(--radius-sm) bg-(--decide-bg) px-3 py-2.5 text-[12.5px] font-semibold text-(--decide)">
          Not calculated yet · {missingCount} value{missingCount > 1 ? "s" : ""}{" "}
          needed below
        </div>
      )}
    </div>
  );
}
